// Single source of truth for admission-list filtering on the client. Mirrors
// api/src/services/admission/listFilter.js — the two must stay aligned or the
// realtime view will disagree with what the server returns.

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const stripDiacritics = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const FR_DATE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

export const serializeFilterSpec = (spec) => {
  const canonical = {};
  for (const key of Object.keys(spec).sort()) {
    const value = spec[key];
    if (value === undefined || value === null || value === "" || value === false) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    canonical[key] = Array.isArray(value) ? [...value].sort() : value;
  }
  return JSON.stringify(canonical);
};

export const buildServerParams = (spec) => {
  const params = new URLSearchParams();
  if (spec.status?.length) params.set("status", spec.status.join(","));
  if (spec.source?.length) params.set("source", spec.source.join(","));
  if (spec.flags?.length) params.set("flags", spec.flags.join(","));
  if (spec.laboratoryId) params.set("laboratoryId", spec.laboratoryId);
  if (spec.date) params.set("date", spec.date);
  if (spec.dateWithinDays) params.set("dateWithinDays", String(spec.dateWithinDays));
  if (spec.search) params.set("search", spec.search);
  if (spec.urgent) params.set("urgent", "true");
  if (spec.silReservationNumber) params.set("silReservationNumber", spec.silReservationNumber);
  if (spec.silAdmissionNumber) params.set("silAdmissionNumber", spec.silAdmissionNumber);
  return params;
};

// Predicate for the socket upsert handler. Must agree with the server query
// produced on the backend. Reads only fields emitted by
// buildAdmissionListRow in api/src/services/admissionListRow.js.
export const matchesFilterSpec = (admission, spec) => {
  if (!admission) return false;

  if (spec.urgent && !(!!admission.checkInDate && admission.status !== "RECORDED" && admission.status !== "ARCHIVED")) return false;
  if (spec.status?.length && !spec.status.includes(admission.status)) return false;
  if (spec.source?.length && !spec.source.includes(admission.source)) return false;
  if (spec.flags?.length) {
    const admissionFlags = admission.flags || [];
    if (!spec.flags.some((flag) => admissionFlags.includes(flag))) return false;
  }
  if (spec.laboratoryId) {
    const lab = admission.laboratory;
    const labId = typeof lab === "string" ? lab : lab?._id || lab?.toString?.();
    if (labId !== spec.laboratoryId) return false;
  }
  if (spec.silReservationNumber && stripDiacritics(admission.silReservationNumber).toLowerCase() !== stripDiacritics(spec.silReservationNumber).toLowerCase()) return false;
  if (spec.silAdmissionNumber && stripDiacritics(admission.silAdmissionNumber).toLowerCase() !== stripDiacritics(spec.silAdmissionNumber).toLowerCase()) return false;
  if (spec.date) {
    const at = admission.submittedAt || admission.createdAt;
    if (!at) return false;
    const start = new Date(`${spec.date}T00:00:00`).getTime();
    const end = new Date(`${spec.date}T23:59:59.999`).getTime();
    const time = new Date(at).getTime();
    if (time < start || time > end) return false;
  } else if (spec.dateWithinDays) {
    const at = admission.submittedAt || admission.createdAt;
    if (!at) return false;
    const floor = Date.now() - spec.dateWithinDays * 24 * 60 * 60 * 1000;
    if (new Date(at).getTime() < floor) return false;
  }
  if (spec.search) {
    const collapsed = spec.search.replace(/\s+/g, "");
    const tokens = /^\d+$/.test(collapsed) ? [collapsed] : spec.search.split(/\s+/).filter(Boolean);
    const patient = admission.patient || {};
    const tokenMatches = (token) => {
      const pattern = new RegExp(`^${escapeRegex(stripDiacritics(token).toLowerCase())}`);
      const candidates = [patient.firstName, patient.lastName, patient.birthLastName, admission.silReservationNumber, admission.silAdmissionNumber, admission.number, patient.INS?.value];
      if (candidates.some((value) => value && pattern.test(stripDiacritics(value).toLowerCase()))) return true;
      const fr = token.match(FR_DATE);
      if (fr) {
        const [, d, m, y] = fr;
        return patient.birthdate === `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
      return false;
    };
    if (!tokens.every(tokenMatches)) return false;
  }
  return true;
};

export const filterSpecsEqual = (a, b) => serializeFilterSpec(a) === serializeFilterSpec(b);
